import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "sonner";
import {
  GetBookingQuery,
  useCancelBookingMutation,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import useAnalytics from "../../../hooks/useAnalytics";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import styled from "../../../styles";
import { Button, ButtonWrap } from "../../CTA";
import { Flex } from "../../Flex";
import Loader from "../../Loader";
import { Underline } from "../../Tabs";
import { Text } from "../../Text";
import { TextArea } from "../../TextArea";
import { View } from "../../View";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

interface Props {
  booking: GetBookingQuery["booking"];
  onCancel: () => void;
}

export const CancelBookingOverlay = (props: Props) => {
  const { track } = useAnalytics();
  const client = useGqlClient();
  const cancelBooking = useCancelBookingMutation(client);
  const brand = useSelector(authSelectors.activeBrand);
  const history = useHistory();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        props.onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props]);

  return (
    <>
      {cancelBooking.isLoading ? (
        <Flex align="center" justify="center">
          <Loader />
        </Flex>
      ) : (
        <StyledFlex>
          <Text
            weight="bold"
            size="xl"
            margin={"0 0 0 0"}
            colorPreset="heading"
          >
            Cancel Booking
          </Text>
          <Underline selected />
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            Let them know why you're cancelling and if you'd like them to apply
            for alternative dates.
          </Text>
          <TextArea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) {
                setError(undefined);
              }
            }}
            margin="l 0 0"
            rows={4}
            error={error}
          />

          <ButtonWrap margin="l 0 0">
            <Button buttonType="secondary" onClick={props.onCancel}>
              Back
            </Button>
            <Button
              isDisabled={reason.length === 0}
              onClick={() => {
                if (reason.length === 0) {
                  setError("Please provide a cancellation reason");
                  return;
                }

                cancelBooking.mutate(
                  {
                    bookingId: props.booking.id,
                    cancellationReason: reason,
                  },
                  {
                    onSuccess: (res) => {
                      toast.success("Booking cancelled");
                      track("Booking cancelled", {
                        id: res.cancelBooking?.booking?.id,
                        brand: brand ? brand.name : undefined,
                      });
                      props.onCancel();
                      queryClient.resetQueries([queryKeys.getBooking], {
                        exact: false,
                      });
                      history.replace("/b/bookings/archived");
                    },
                    onError: (error: any) => {
                      console.error("error: ", error);
                    },
                  }
                );
              }}
            >
              Cancel Booking
            </Button>
          </ButtonWrap>
        </StyledFlex>
      )}
    </>
  );
};

export const Alert = styled(View)`
  background-color: ${(p) => p.theme.color.secondary}30;
  border-left: 3px solid ${(p) => p.theme.color.secondary};
  border-radius: 2px;
  width: 100%;
  box-sizing: border-box;

  color: ${(p) => p.theme.color.secondary};
`;
